import type { AspectRatio } from '@lumen5/luminary';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { NarrativeThreadEnum } from 'js/src/client';

export interface SketchControlsState {
    blueprintId: number | null;
    templateId: string | null;
    narrativeThread: NarrativeThreadEnum | null;
    durationSeconds: number | null;
    aspectRatio: AspectRatio | null;
    voiceId: string | null;
    brandKitId: number | null;
    projectId: number | null;
    cookiePersistenceEnabled: boolean;
    /** Whether a build has applied the brand kit since it was picked. Never saved. */
    brandKitApplied: boolean;
}

const initialState: SketchControlsState = {
    blueprintId: null,
    templateId: null,
    narrativeThread: null,
    durationSeconds: null,
    aspectRatio: null,
    voiceId: null,
    brandKitId: null,
    projectId: null,
    cookiePersistenceEnabled: true,
    brandKitApplied: false,
};

/**
 * A blueprint (or a past video being matched) picks the video's template, format, brand kit and
 * voice, so picking one starts those over; the user can then override them. It also fixes the
 * narrative thread, so the thread and a blueprint are never set together.
 */
function clearDesignSettings(state: SketchControlsState) {
    state.templateId = null;
    state.narrativeThread = null;
    state.aspectRatio = null;
    state.voiceId = null;
    state.brandKitId = null;
}

const sketchControlsSlice = createSlice({
    name: 'sketchControls',
    initialState,
    reducers: {
        setBlueprint: (state, action: PayloadAction<number>) => {
            state.blueprintId = action.payload;
            clearDesignSettings(state);
        },
        clearBlueprint: (state) => {
            state.blueprintId = null;
        },
        /**
         * A past video picked to match decides the design as a blueprint does. The video itself
         * isn't kept here: like a recipe, it rides only the build it was picked for.
         */
        clearForPastVideoMatch: (state) => {
            state.blueprintId = null;
            clearDesignSettings(state);
        },
        setTemplate: (state, action: PayloadAction<string>) => {
            state.templateId = action.payload;
        },
        clearTemplate: (state) => {
            state.templateId = null;
        },
        setNarrativeThread: (state, action: PayloadAction<NarrativeThreadEnum>) => {
            state.narrativeThread = action.payload;
            state.blueprintId = null;
        },
        clearNarrativeThread: (state) => {
            state.narrativeThread = null;
        },
        setDuration: (state, action: PayloadAction<number>) => {
            state.durationSeconds = action.payload;
        },
        clearDuration: (state) => {
            state.durationSeconds = null;
        },
        setAspectRatio: (state, action: PayloadAction<AspectRatio>) => {
            state.aspectRatio = action.payload;
        },
        clearAspectRatio: (state) => {
            state.aspectRatio = null;
        },
        setVoice: (state, action: PayloadAction<string>) => {
            state.voiceId = action.payload;
        },
        clearVoice: (state) => {
            state.voiceId = null;
        },
        setBrandKit: (state, action: PayloadAction<number>) => {
            state.brandKitId = action.payload;
            state.brandKitApplied = false;
        },
        markBrandKitApplied: (state) => {
            state.brandKitApplied = true;
        },
        /**
         * The brand kit reaches a build only through this store, never through the message
         * (unlike the other settings), so it is spent only once a build has applied it.
         */
        clearBrandKitIfApplied: (state) => {
            if (state.brandKitApplied) {
                state.brandKitId = null;
                state.brandKitApplied = false;
            }
        },
        clearBrandKit: (state) => {
            state.brandKitId = null;
        },
        setProject: (state, action: PayloadAction<number>) => {
            state.projectId = action.payload;
        },
        clearProject: (state) => {
            state.projectId = null;
        },
        loadFromCookie: (state, action: PayloadAction<Partial<SketchControlsState>>) => {
            state.blueprintId = action.payload.blueprintId ?? null;
            state.templateId = action.payload.templateId ?? null;
            state.narrativeThread = action.payload.narrativeThread ?? null;
            state.durationSeconds = action.payload.durationSeconds ?? null;
            state.aspectRatio = action.payload.aspectRatio ?? null;
            state.voiceId = action.payload.voiceId ?? null;
            state.brandKitId = action.payload.brandKitId ?? null;
            state.projectId = action.payload.projectId ?? null;
            // Cookies saved before a blueprint fixed the thread can hold both.
            if (state.blueprintId) {
                state.narrativeThread = null;
            }
        },
        clearAllControls: (state) => {
            state.blueprintId = null;
            state.templateId = null;
            state.narrativeThread = null;
            state.durationSeconds = null;
            state.aspectRatio = null;
            state.voiceId = null;
            // Note: brandKitId is intentionally NOT cleared here, so CreateVideoTool can
            // still fetch and apply it. projectId is kept for the same reason: the video
            // row is created at turn end and must still land in the chosen project.
        },
        disableCookiePersistence: (state) => {
            state.cookiePersistenceEnabled = false;
        },
        enableCookiePersistence: (state) => {
            state.cookiePersistenceEnabled = true;
        },
    },
});

export const {
    setBlueprint,
    clearBlueprint,
    clearForPastVideoMatch,
    setTemplate,
    clearTemplate,
    setNarrativeThread,
    clearNarrativeThread,
    setDuration,
    clearDuration,
    setAspectRatio,
    clearAspectRatio,
    setVoice,
    clearVoice,
    setBrandKit,
    clearBrandKit,
    markBrandKitApplied,
    clearBrandKitIfApplied,
    setProject,
    clearProject,
    loadFromCookie,
    clearAllControls,
    disableCookiePersistence,
    enableCookiePersistence,
} = sketchControlsSlice.actions;

export default sketchControlsSlice.reducer;
